// Cuentas Incobrables — saldos que salen de la cobranza del día a día porque se
// gestionan por fuera (extrajudicial, despacho, acuerdo aparte). El dinero sigue
// siendo nuestro y sigue registrado; lo que cambia es quién lo persigue y cómo.
//
// Same RPC and same grain as Saldo a Hoy with p_scope flipped, so the two
// reports always partition the same money: nothing can sit in both, nothing
// falls between them.
//
// A reservation lands here when someone with the `reservations.write_off`
// permission marks it from its detail page. "Días" counts from that mark — the
// antiquity is how long lleva en gestión aparte, nothing is inferred.
import supabase from "../../../supabase-client";
import { formatFriendlyDate, RESERVATION_STATUS_ORDER } from "../../../utility/funcs";

const fmtMoney = (v) => "$" + Math.round(Number(v) || 0).toLocaleString("es-MX");
const fmtDate = (v) => (v ? formatFriendlyDate(v) : "");
const fmtDays = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n === 1 ? "1 día" : `${n.toLocaleString("es-MX")} días`;
};

export const cuentasIncobrablesReport = {
  id: "cuentas-incobrables",
  title: "Cuentas Incobrables",
  description:
    "Reservas cuya cobranza se lleva por fuera del proceso normal. Salen de Saldo a Hoy para no mezclarse con la cobranza del día a día; aquí se les da seguimiento aparte, con la antigüedad desde que se separaron.",
  category: "Finanzas",
  // Oldest write-offs first — the ones least likely to ever be recovered.
  defaultSort: { key: "days_written_off", direction: "descending" },
  filters: [
    { type: "facet", key: "customer_name", label: "Cliente", column: "customer_name" },
    { type: "facet", key: "status", label: "Estatus", column: "status", order: RESERVATION_STATUS_ORDER },
    { type: "facet", key: "vendor_name", label: "Asesor", column: "vendor_name" },
  ],
  summary: [
    { type: "count", label: "Reservas en gestión aparte" },
    { type: "sum", label: "Devengado a hoy", valueKey: "saldo_a_hoy", valueFormat: fmtMoney },
    { type: "sum", label: "Saldo total por cobrar", valueKey: "balance", valueFormat: fmtMoney },
    {
      type: "groupBy",
      label: "Por Cliente",
      key: "customer_name",
      valueKey: "balance",
      valueFormat: fmtMoney,
      format: (v) => v || "Sin cliente",
    },
  ],
  columns: [
    { key: "customer_name", label: "Cliente", width: 240, truncate: true, groupable: true, groupNoun: "reserva" },
    { key: "reservation_id", label: "Reserva", format: (v) => `#${v}` },
    { key: "car", label: "Auto", width: 220, truncate: true },
    { key: "status", label: "Estatus", sortOrder: RESERVATION_STATUS_ORDER },
    { key: "vendor_name", label: "Asesor" },
    { key: "start_date", label: "Entrega", format: fmtDate, exportType: "date" },
    { key: "end_date", label: "Devolución", format: fmtDate, exportType: "date" },
    { key: "total_price", label: "Total", format: fmtMoney, subtotal: true, exportType: "currency" },
    { key: "paid", label: "Pagado", format: fmtMoney, subtotal: true, exportType: "currency" },
    // Devengado = lo que ya se había consumido; Saldo total = todo el contrato.
    { key: "saldo_a_hoy", label: "Devengado a hoy", format: fmtMoney, subtotal: true, exportType: "currency" },
    { key: "balance", label: "Saldo total", format: fmtMoney, subtotal: true, exportType: "currency" },
    { key: "written_off_at", label: "Separado el", format: fmtDate, exportType: "date" },
    { key: "days_written_off", label: "Días en gestión", format: fmtDays, exportType: "integer" },
    { key: "written_off_notes", label: "Nota", width: 260, truncate: true },
  ],
  async fetch() {
    const { data, error } = await supabase.rpc("report_saldo_a_hoy", {
      p_vendor: null,
      p_scope: "incobrable",
    });
    if (error) throw error;
    return data || [];
  },
};
